import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    useTheme,
    useMediaQuery,
    Divider,
    Typography,
    Tooltip,
    Collapse
} from '@mui/material';
import {
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    Home as HomeIcon,
    DateRange as CalendarIcon,
    Folder as FolderIcon,
    WebStories as WebStoriesIcon,
    PostAdd as PostIcon,
    Person as ProfileIcon,
    Settings as SettingsIcon,
    ExpandLess,
    ExpandMore,
    Campaign as CampaignIcon,
    Groups as GroupsIcon,
    Autorenew as RecycleIcon,
    Speed as StrategyIcon,
    Analytics as AnalyticsIcon
} from '@mui/icons-material';
import CategoryIcon from '@mui/icons-material/Category';
import { styled } from '@mui/material/styles';
import { useAuth } from '../providers/AuthProvider';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { logError } from '../utils/logger';
import { ROUTES } from '../constants';
import SoloSocialLogo from '../components/SoloSocialLogo';

// Styled components
const StyledDrawer = styled(Drawer, {
    shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth',
})(({ theme, open, drawerWidth }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        width: drawerWidth,
        '& .MuiDrawer-paper': {
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
        },
    }),
    ...(!open && {
        '& .MuiDrawer-paper': {
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
            width: theme.spacing(7),
            [theme.breakpoints.up('sm')]: {
                width: theme.spacing(9),
            },
        },
    }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
    minHeight: 48,
    justifyContent: 'initial',
    px: 2.5,
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0.5, 1),
    '&.Mui-selected': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '& .MuiListItemIcon-root': {
            color: theme.palette.primary.contrastText,
        },
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

// Styled components for logo and branding
const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    cursor: 'pointer',
    '&:hover': {
        opacity: 0.8
    }
}));

const BetaTag = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.grey[200],
    padding: '2px 6px',
    borderRadius: theme.spacing(0.5),
    fontSize: '0.6rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
}));

const DRAWER_WIDTH = 280;
const MOBILE_DRAWER_WIDTH = 280;

/**
 * SideNavBar Component
 * 
 * A responsive side navigation component that:
 * - Shows as a collapsible sidebar on desktop
 * - Shows as a temporary drawer on mobile
 * - Provides keyboard navigation and accessibility
 * - Integrates with feature access control
 * 
 * @component
 * @example
 * return (
 *   <SideNavBar 
 *     mobileOpen={mobileOpen} 
 *     onMobileToggle={handleMobileToggle}
 *   />
 * )
 */
const SideNavBar = ({ mobileOpen, onMobileToggle, desktopOpen, onDesktopToggle }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isAuthenticated, logout } = useAuth();
    const {
        canAccessTemplateEditor,
        canCreatePosts,
        canAccessSocialConnections
    } = useFeatureAccess();

    // Local state for create menu
    const [createMenuOpen, setCreateMenuOpen] = useState(false);

    /**
     * Gets the display name for the current user
     * @returns {string} The user's display name (first name + last name, or username, or 'User')
     */
    const getUserDisplayName = () => {
        if (!user || !user[0]) return 'User';
        
        const userData = user[0];
        const firstName = userData.first_name?.trim();
        const lastName = userData.last_name?.trim();
        
        // If we have both first and last name, use them
        if (firstName && lastName) {
            return `${firstName} ${lastName}`;
        }
        
        // If we only have first name, use it
        if (firstName) {
            return firstName;
        }
        
        // If we only have last name, use it
        if (lastName) {
            return lastName;
        }
        
        // Fall back to username if no names are available
        return userData.username || 'User';
    };

    // Navigation items configuration
    const navigationItems = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            icon: <HomeIcon />,
            path: '/dashboard',
            section: 'main'
        },
        {
            id: 'calendar',
            label: 'Calendar',
            icon: <CalendarIcon />,
            path: '/calendar',
            section: 'main',
            requiresFeature: canCreatePosts
        },
        {
            id: 'content-buckets',
            label: 'Content Buckets',
            icon: <FolderIcon />,
            path: '/content-buckets',
            section: 'main',
            requiresFeature: canCreatePosts
        },
        {
            id: 'templates',
            label: 'Templates',
            icon: <WebStoriesIcon />,
            path: '/templates',
            section: 'main',
            requiresFeature: canAccessTemplateEditor
        },
        {
            id: 'campaigns',
            label: 'Campaigns',
            icon: <GroupsIcon />,
            path: '/campaigns',
            section: 'main',
            requiresFeature: canCreatePosts
        }
    ];

    const analyticsItems = [
        {
            id: 'analytics-dashboard',
            label: 'Analytics Overview',
            icon: <AnalyticsIcon />,
            path: '/analytics',
            section: 'analytics',
            requiresFeature: canCreatePosts
        },
        {
            id: 'content-recycling',
            label: 'Content Recycling',
            icon: <RecycleIcon />,
            path: '/content-recycling',
            section: 'analytics',
            requiresFeature: canCreatePosts
        },
        {
            id: 'platform-strategy',
            label: 'Platform Strategy',
            icon: <StrategyIcon />,
            path: '/platform-strategy',
            section: 'analytics',
            requiresFeature: canCreatePosts
        }
    ];

    const createItems = [
        {
            id: 'create-post',
            label: 'Create Post',
            icon: <PostIcon />,
            path: '/post/create',
            requiresFeature: canCreatePosts
        },
        {
            id: 'create-campaign',
            label: 'Create Campaign',
            icon: <CampaignIcon />,
            path: '/campaigns/create',
            requiresFeature: canCreatePosts
        },
        {
            id: 'create-template',
            label: 'Editor',
            icon: <CategoryIcon />,
            path: '/editor',
            requiresFeature: canAccessTemplateEditor
        }
    ];

    const profileItems = [
        {
            id: 'profile',
            label: 'Profile',
            icon: <ProfileIcon />,
            path: '/profile',
            section: 'profile'
        }
    ];

    /**
     * Handles navigation to a specific route
     * @param {string} path - The path to navigate to
     * @param {Event} event - The click event
     */
    const handleNavigation = (path, event) => {
        if (event) {
            event.preventDefault();
        }

        // Direct navigation to the path
        navigate(path);

        // Close mobile drawer after navigation
        if (isMobile && onMobileToggle) {
            onMobileToggle();
        }
    };

    /**
     * Checks if a navigation item is currently active
     * @param {Object} item - The navigation item
     * @returns {boolean} Whether the item is active
     */
    const isItemActive = (item) => {
        // Direct path matching - check if the current location path matches the item path
        return location.pathname === item.path;
    };

    /**
     * Handles keyboard navigation
     * @param {KeyboardEvent} event - The keyboard event
     * @param {Function} action - The action to perform
     */
    const handleKeyDown = (event, action) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            action();
        }
    };

    /**
     * Handles user logout
     * @param {Event} event - The click event
     */
    const handleLogout = useCallback((event) => {
        event.preventDefault();
        try {
            logout();
            navigate(ROUTES.HOME, { replace: true });
        } catch (error) {
            logError('Logout error', error);
        }
    }, [logout, navigate]);

    /**
     * Renders a navigation item
     * @param {Object} item - The navigation item configuration
     * @param {boolean} nested - Whether this is a nested item
     * @returns {JSX.Element} The rendered navigation item
     */
    const renderNavItem = (item, nested = false) => {
        // Check feature access
        if (item.requiresFeature !== undefined && !item.requiresFeature) {
            return null;
        }

        const isActive = isItemActive(item);
        const isCollapsed = !(desktopOpen || isMobile);
        
        const itemContent = (
            <StyledListItemButton
                selected={isActive}
                onClick={(event) => handleNavigation(item.path, event)}
                onKeyDown={(event) => handleKeyDown(event, () => handleNavigation(item.path))}
                sx={{
                    pl: nested ? 4 : 2.5,
                    minHeight: 48,
                }}
                role="button"
                tabIndex={0}
                aria-label={`Navigate to ${item.label}`}
                aria-current={isActive ? 'page' : undefined}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 0,
                        mr: desktopOpen || isMobile ? 3 : 'auto',
                        justifyContent: 'center',
                    }}
                >
                    {item.icon}
                </ListItemIcon>
                <ListItemText 
                    primary={item.label}
                    sx={{ 
                        opacity: desktopOpen || isMobile ? 1 : 0,
                        '& .MuiTypography-root': {
                            fontWeight: isActive ? 600 : 400
                        }
                    }}
                />
            </StyledListItemButton>
        );

        return (
            <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
                {!desktopOpen && !isMobile ? (
                    <Tooltip title={item.label} placement="right" arrow>
                        {itemContent}
                    </Tooltip>
                ) : (
                    itemContent
                )}
            </ListItem>
        );
    };

    /**
     * Handles the create menu toggle
     */
    const handleCreateMenuToggle = () => {
        setCreateMenuOpen(!createMenuOpen);
    };

    /**
     * Renders the create menu section
     */
    const renderCreateMenu = () => {
        const availableCreateItems = createItems.filter(item => 
            item.requiresFeature === undefined || item.requiresFeature
        );

        if (availableCreateItems.length === 0) {
            return null;
        }

        const isCollapsed = !(desktopOpen || isMobile);

        return (
            <>
                <ListItem disablePadding sx={{ display: 'block' }}>
                    <Typography
                        variant="caption"
                        sx={{
                            px: 2.5,
                            py: 1,
                            color: 'text.secondary',
                            opacity: desktopOpen || isMobile ? 1 : 0,
                            display: 'block'
                        }}
                    >
                        Create
                    </Typography>
                </ListItem>
                {availableCreateItems.map(item => renderNavItem(item))}
            </>
        );
    };

    /**
     * Renders the analytics menu section
     */
    const renderAnalyticsMenu = () => {
        const availableAnalyticsItems = analyticsItems.filter(item => 
            item.requiresFeature === undefined || item.requiresFeature
        );

        if (availableAnalyticsItems.length === 0) {
            return null;
        }

        const isCollapsed = !(desktopOpen || isMobile);

        return (
            <>
                <ListItem disablePadding sx={{ display: 'block' }}>
                    <Typography
                        variant="caption"
                        sx={{
                            px: 2.5,
                            py: 1,
                            color: 'text.secondary',
                            opacity: desktopOpen || isMobile ? 1 : 0,
                            display: 'block'
                        }}
                    >
                        Analytics & Intelligence
                    </Typography>
                </ListItem>
                {availableAnalyticsItems.map(item => renderNavItem(item))}
            </>
        );
    };

    const drawerContent = (
        <Box
            role="navigation"
            aria-label="Main navigation"
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
            {/* Header */}
            <DrawerHeader>
                {(desktopOpen || isMobile) && (
                    <LogoContainer 
                        onClick={() => handleNavigation('/dashboard')}
                        sx={{ flexGrow: 1, pl: 1 }}
                    >
                        <SoloSocialLogo 
                            fontSize="medium" 
                            color="primary"
                        />
                        <Typography 
                            variant="h6" 
                            noWrap 
                            component="div" 
                            color="primary"
                            sx={{ 
                                ml: 1,
                                fontWeight: 600,
                                fontSize: '1.1rem'
                            }}
                        >
                            Solo Social
                        </Typography>
                        <BetaTag>
                            Beta
                        </BetaTag>
                    </LogoContainer>
                )}
                {!isMobile && (
                    <IconButton 
                        onClick={onDesktopToggle}
                        aria-label={desktopOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                        size="small"
                        sx={{ 
                            ml: (desktopOpen || isMobile) ? 0 : 'auto',
                            mr: (desktopOpen || isMobile) ? 0 : 'auto'
                        }}
                    >
                        {desktopOpen ? <ChevronLeftIcon /> : <MenuIcon />}
                    </IconButton>
                )}
            </DrawerHeader>

            <Divider />

            {/* Main Navigation */}
            <List sx={{ flexGrow: 1, pt: 1 }}>
                {/* Main navigation items */}
                {navigationItems.map(item => renderNavItem(item))}
                
                <Divider sx={{ my: 1 }} />
                
                {/* Analytics menu */}
                {renderAnalyticsMenu()}
                
                <Divider sx={{ my: 1 }} />
                
                {/* Create menu */}
                {renderCreateMenu()}
                
                <Divider sx={{ my: 1 }} />
                
                {/* Profile items */}
                {profileItems.map(item => renderNavItem(item))}
            </List>

            {/* User info at bottom */}
            {(desktopOpen || isMobile) && user && (
                <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Signed in as
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium" noWrap>
                                {getUserDisplayName()}
                            </Typography>
                            <Typography 
                                component="button"
                                variant="caption"
                                onClick={handleLogout}
                                onKeyDown={(event) => handleKeyDown(event, handleLogout)}
                                sx={{
                                    color: 'text.secondary',
                                    cursor: 'pointer',
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        color: 'primary.main',
                                    },
                                    '&:focus': {
                                        outline: `2px solid ${theme.palette.primary.main}`,
                                        outlineOffset: '2px',
                                    }
                                }}
                                tabIndex={0}
                                aria-label="Logout"
                                role="button"
                            >
                                (logout)
                            </Typography>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );

    if (!isAuthenticated) {
        return null;
    }

    return (
        <>
            {/* Mobile Drawer */}
            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={onMobileToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: MOBILE_DRAWER_WIDTH,
                        },
                    }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="mobile-drawer-title"
                >
                    {drawerContent}
                </Drawer>
            )}

            {/* Desktop Drawer */}
            {!isMobile && (
                <StyledDrawer
                    variant="permanent"
                    open={desktopOpen}
                    drawerWidth={DRAWER_WIDTH}
                    sx={{
                        display: { xs: 'none', md: 'block' },
                    }}
                >
                    {drawerContent}
                </StyledDrawer>
            )}
        </>
    );
};

export default SideNavBar; 